// Batch import. Legacy: processBatchFile/fetchGoogleSheet/parseBatchPaste (L7559-7668).
// Uses SheetJS (xlsx) exactly as the legacy editor did. (Phase 3 note: npm xlsx is
// pinned to 0.18.5; the legacy CDN build was 0.20.1 — switch to the SheetJS registry
// build before production for the security fixes. API is identical for our usage.)
import * as XLSX from 'xlsx'
import type { BatchRow } from './../types'

export interface ParsedBatch {
  headers: string[]
  rows: BatchRow[]
}

function workbookToBatch(wb: XLSX.WorkBook): ParsedBatch {
  const sheet = wb.Sheets[wb.SheetNames[0]]
  const rows = XLSX.utils.sheet_to_json<BatchRow>(sheet, { defval: '' })
  if (rows.length === 0) throw new Error('File is empty or has no data rows')
  return { headers: Object.keys(rows[0]), rows }
}

/** Parse CSV/TSV/pasted text. */
export function parseDelimitedText(text: string): ParsedBatch {
  return workbookToBatch(XLSX.read(text, { type: 'string' }))
}

/** Parse an .xlsx/.xls ArrayBuffer. */
export function parseSpreadsheetBuffer(buf: ArrayBuffer): ParsedBatch {
  return workbookToBatch(XLSX.read(new Uint8Array(buf), { type: 'array' }))
}

/** Parse a dropped/selected File (csv/tsv read as text, xlsx as binary). */
export function parseBatchFile(file: File): Promise<ParsedBatch> {
  const isText = /\.(csv|tsv)$/i.test(file.name)
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onerror = () => reject(reader.error)
    reader.onload = () => {
      try {
        resolve(isText
          ? parseDelimitedText(typeof reader.result === 'string' ? reader.result : new TextDecoder().decode(reader.result as ArrayBuffer))
          : parseSpreadsheetBuffer(reader.result as ArrayBuffer))
      } catch (err) {
        reject(err)
      }
    }
    if (isText) reader.readAsText(file)
    else reader.readAsArrayBuffer(file)
  })
}

/** Build the Google Sheets CSV export URL from a share URL (legacy fetchGoogleSheet). */
export function googleSheetCsvUrl(url: string): string | null {
  const id = url.match(/\/spreadsheets\/d\/([a-zA-Z0-9_-]+)/)
  if (!id) return null
  const gid = url.match(/[#&?]gid=(\d+)/)?.[1] ?? '0'
  return `https://docs.google.com/spreadsheets/d/${id[1]}/export?format=csv&gid=${gid}`
}

export async function fetchGoogleSheet(url: string): Promise<ParsedBatch> {
  const csvUrl = googleSheetCsvUrl(url)
  if (!csvUrl) throw new Error('Invalid Google Sheets URL')
  const res = await fetch(csvUrl)
  if (!res.ok) throw new Error('Sheet not accessible — set sharing to "Anyone with the link can view".')
  return parseDelimitedText(await res.text())
}
